import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, effect, inject, input, signal, viewChild } from '@angular/core';
import { NgClass, SlicePipe, NgOptimizedImage } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { SharedModule } from '../../../../sub-modules/SharedModule';
import { UploadFileComponent } from '../../../../shared/components/mtm-upload-file/upload-file.component';
import { UploadModalComponent } from '../../../../on-boarding/profile-avatar/upload-modal/upload-modal.component';
import { WorkspacePermissionsImgUploadService } from '../workspace-permissions/services/workspace-permissions-img-upload.service';
import { WorkspacePermissionHelperService } from '../workspace-permissions/services/workspace-permissions-helper.service';
import { WorkspaceService } from '../../../../shared/services/workspace.service';
import { PermissionService } from '../../../../shared/services/permissions.service';
import { TimelineService } from '../../../../shared/services/timeline.service';
import { LogService } from '../../../../shared/services/log.service';
import { noHtmlValidator } from '../../../../shared/validators/nohtml.validator';
import { Workspace } from '../../../../shared/interfaces';
import { AvatarImageResult, SaveWorkspaceResult } from '../../../models/workspace.model';
import { TRANSLATION_KEYS, WORKSPACE_KEYS, WORKSPACE_MODAL_CONFIG } from '../../constants/workspace.constants';

/**
 * Workspace avatar + name header for the edit-workspace page. Extracted from the legacy
 * EditWorkspacePermissionsComponent header so it stays available when the new permission UI
 * (`mtm-account-page`) replaces that component.
 */
@Component({
  selector: 'mtm-workspace-general-info',
  standalone: true,
  imports: [NgClass, SlicePipe, NgOptimizedImage, ReactiveFormsModule, SharedModule],
  templateUrl: './workspace-general-info.component.html',
  styleUrl: './workspace-general-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspaceGeneralInfoComponent implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly modalService = inject(NgbModal);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly permissionService = inject(PermissionService);
  private readonly imgUploadService = inject(WorkspacePermissionsImgUploadService);
  private readonly permissionHelper = inject(WorkspacePermissionHelperService);
  private readonly logService = inject(LogService);
  readonly timelineService = inject(TimelineService);

  readonly workspace = input.required<Workspace>();
  private readonly avatarUploader = viewChild<UploadFileComponent>('avatarUploader');

  // The reused upload/save services take an `ngUnsubscribe` observable for takeUntil.
  private readonly destroyed$ = new Subject<void>();
  private readonly cacheBuster = Date.now().toString();
  private uploadModal: NgbModalRef;
  private _workspace: Workspace | null = null;

  readonly avatarUrl = signal<string>('');
  readonly workspaceName = signal<string>('');
  readonly nameValue = signal<string>('');
  readonly canUpdateWorkspace = signal<boolean>(false);

  readonly headerForm: UntypedFormGroup = this.fb.group({
    [WORKSPACE_KEYS.NAME]: ['', [Validators.required, noHtmlValidator]],
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed$.next();
      this.destroyed$.complete();
    });
    effect(() => this.applyWorkspace(this.workspace()));
  }

  public ngOnInit(): void {
    this.canUpdateWorkspace.set(this.permissionService.canUpdateWorkspace());
    if (!this.canUpdateWorkspace()) this.headerForm.get(WORKSPACE_KEYS.NAME).disable();
    // The mobile "Save Changes" button in EditWorkspaceComponent reads this form.
    this.workspaceService.formValue = this.headerForm;
    this.setupAutoSave();
  }

  public editAvatar(): void {
    this.avatarUploader()?.upload();
  }

  public fileChangeListener($event: { files: File[] }): void {
    const file = $event.files?.[0];
    if (!file) return;
    this.openModal();
    this.uploadModal.componentInstance.imageFile = file;
    const reader: FileReader = new FileReader();
    reader.onloadend = (): void => this.setAvatarImage();
    reader.readAsDataURL(file);
  }

  public onImageError(): void {
    this.avatarUrl.set('');
  }

  public removeLeadingSpaces(): void {
    const control = this.headerForm.get(WORKSPACE_KEYS.NAME);
    control.setValue(control.value.trimStart().replace(WORKSPACE_KEYS.INVALID_CHARS_REGEX, ''), { emitEvent: false });
    this.nameValue.set(control.value);
  }

  public isNameInvalid(): boolean {
    const control = this.headerForm.get(WORKSPACE_KEYS.NAME);
    return control?.invalid && control?.touched;
  }

  private applyWorkspace(workspace: Workspace): void {
    if (!workspace?.id) return;
    this._workspace = workspace;
    this.workspaceName.set(workspace.name || '');
    this.nameValue.set(workspace.name || '');
    this.avatarUrl.set(
      workspace.avatarUrl ? workspace.avatarUrl + WORKSPACE_KEYS.CACHE_BUSTER_PARAM + this.cacheBuster : ''
    );
    // emitEvent:false so re-applying the loaded workspace never triggers an autosave.
    this.headerForm.patchValue({ [WORKSPACE_KEYS.NAME]: workspace.name || '' }, { emitEvent: false });
  }

  private setupAutoSave(): void {
    this.headerForm
      .get(WORKSPACE_KEYS.NAME)
      .valueChanges.pipe(
        debounceTime(WORKSPACE_KEYS.AUTOSAVE_DEBOUNCE),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        if (!this.headerForm.invalid && this._workspace) this.saveWorkspace();
      });
  }

  private saveWorkspace(): void {
    this.permissionHelper
      .saveWorkspace({
        workspace: this._workspace,
        form: this.headerForm,
        trimString: (value: string) => value?.trim() ?? '',
        ngUnsubscribe: this.destroyed$,
        isPending: false,
      })
      .then((result: SaveWorkspaceResult) => {
        if (result.success && result.updatedWorkspace) {
          this._workspace = result.updatedWorkspace;
          this.workspaceName.set(result.updatedWorkspace.name || '');
        }
      })
      .catch((err: Error) =>
        this.logService.logError(err, {
          message: TRANSLATION_KEYS.WORKSPACE_EDIT_ERROR,
          workspaceId: this._workspace?.id,
        })
      );
  }

  private openModal(): void {
    this.uploadModal = this.modalService.open(UploadModalComponent, { size: WORKSPACE_MODAL_CONFIG.SIZE });
    this.uploadModal.componentInstance.resizeToWidth = WORKSPACE_MODAL_CONFIG.AVATAR_WIDTH;
    this.uploadModal.componentInstance.aspectRatio = WORKSPACE_MODAL_CONFIG.AVATAR_ASPECT_RATIO;
    this.uploadModal.componentInstance.maintainAspectRatio = true;
  }

  private setAvatarImage(): void {
    this.imgUploadService
      .setAvatarImageOnModal({
        uploadModal: this.uploadModal,
        cacheBuster: this.cacheBuster,
        workspace: this._workspace,
        ngUnsubscribe: this.destroyed$,
      })
      .then((result: AvatarImageResult) => {
        if (result?.avatarUrl) this.avatarUrl.set(result.avatarUrl);
        if (this._workspace && result?.workspaceAvatarUrl) this._workspace.avatarUrl = result.workspaceAvatarUrl;
      });
  }
}
